import { Fragment, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { Box, Typography } from '@mui/material';
import { Banner, Divider, IndexTable } from '@shopify/polaris';
import { confirmable } from 'react-confirm';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import i18n from 'src/locales/i18n';
import FormProvider from 'src/components/hook_form/form_provider';
import RHFTextField from 'src/components/hook_form/rhf_text_field';
import { SelectCurrency } from 'src/components/hook_form/select_currency';
import { formatDateTime } from 'src/utils/format_time';
import { getOrdinal } from 'src/utils/format_data';
import { colStyle, colCellStyle } from 'src/components/table/column_width';
import StackedModal from './stacked_modal';
import {
  EMPTY_CURRENCY_META,
  FEE_KEYS,
  feeLabelKey,
  feeModalSx,
  formatFee,
  toFeeNumber,
  useCurrencyMap,
} from './return_fee_dialog';

i18n.loadNamespaces(['return_inbound', 'common', 'placeholder', 'validation']);

const ROWS_VISIBLE = 7;
const ROW_HEIGHT = 76;
const HEADER_HEIGHT = 40;

const SUMMARY_COLUMNS = '180px 200px repeat(3, minmax(140px, 1fr))';

const toRow = (receiving) => ({
  receivingSequence: receiving.receivingSequence,
  completedDate: receiving.completedDate,
  codFee: receiving.codFee ?? '',
  returnFee: receiving.returnFee ?? '',
  prepaidAmount: receiving.prepaidAmount ?? '',
  currencyCode: receiving.currencyCode ?? '',
});

const ReturnFeeEditDialog = ({ show, proceed, receivings = [], onSave }) => {
  const currencyMap = useCurrencyMap();
  const metaOf = (code) => currencyMap[code] ?? EMPTY_CURRENCY_META;

  const schema = useMemo(
    () =>
      Yup.object().shape({
        rows: Yup.array().of(
          Yup.object().shape({
            currencyCode: Yup.string().test(
              'currency-required',
              i18n.t('validation:currency_required'),
              function (value) {
                const hasFee = FEE_KEYS.some((key) => toFeeNumber(this.parent[key]) !== null);
                return !hasFee || Boolean(value);
              },
            ),
          }),
        ),
      }),
    [],
  );

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: { rows: (receivings ?? []).map(toRow) },
  });
  const { errors, isDirty } = methods.formState;
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const { fields } = useFieldArray({ control: methods.control, name: 'rows' });
  const rows = useWatch({ control: methods.control, name: 'rows' }) ?? [];

  const hasAnyFee = (list) =>
    list.some((row) => FEE_KEYS.some((key) => toFeeNumber(row[key]) !== null));

  const totals = [...new Set(rows.map((row) => row.currencyCode))]
    .map((code) => ({ currencyCode: code, group: rows.filter((r) => r.currencyCode === code) }))
    .filter(({ group }) => hasAnyFee(group))
    .map(({ currencyCode, group }) => ({
      currencyCode: currencyCode,
      sums: Object.fromEntries(
        FEE_KEYS.map((key) => [key, group.reduce((acc, r) => acc + (Number(r[key]) || 0), 0)]),
      ),
    }));

  const rowMarkup = fields.map((field, index) => {
    const meta = metaOf(rows[index]?.currencyCode ?? field.currencyCode);

    return (
      <IndexTable.Row
        id={String(field.receivingSequence)}
        key={field.id}
        position={index}
        onClick={() => {}}
      >
        <Box
          component={IndexTable.Cell}
          sx={{ ...colStyle(180), verticalAlign: 'top' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', pt: 'var(--p-space-100)' }}>
            <Typography
              variant="body_md_medium"
              color="text.secondary"
            >
              {i18n.t('return_inbound:fee.nth', { ordinal: getOrdinal(field.receivingSequence) })}
            </Typography>
            <Typography
              variant="body_sm"
              color="text.secondary"
            >
              {field.completedDate ? formatDateTime(field.completedDate) : '-'}
            </Typography>
          </Box>
        </Box>

        <td
          onClick={(e) => e.stopPropagation()}
          style={{ ...colCellStyle(200), verticalAlign: 'top' }}
        >
          <Box sx={{ width: '100%' }}>
            <SelectCurrency
              value={rows[index]?.currencyCode ?? field.currencyCode}
              setValue={(value) => {
                methods.setValue(`rows.${index}.currencyCode`, value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                const nextDecimals = metaOf(value).decimals;
                FEE_KEYS.forEach((key) => {
                  const current = methods.getValues(`rows.${index}.${key}`);
                  if (current === '' || current === null || current === undefined) return;
                  methods.setValue(`rows.${index}.${key}`, toFeeNumber(current, nextDecimals), {
                    shouldDirty: true,
                  });
                });
              }}
              placeholder={i18n.t('placeholder:currency')}
              hideSearchIcon
              dropdownFitContent
              valueAsCode
              error={!!errors?.rows?.[index]?.currencyCode}
            />
            {errors?.rows?.[index]?.currencyCode && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.5,
                  color: 'var(--app-icon-critical)',
                }}
              >
                <ErrorOutlineOutlinedIcon sx={{ fontSize: '0.875rem' }} />
                <Typography variant="body_sm">{errors.rows[index].currencyCode.message}</Typography>
              </Box>
            )}
          </Box>
        </td>

        {FEE_KEYS.map((key) => (
          <td
            key={key}
            onClick={(e) => e.stopPropagation()}
            style={{ padding: 'var(--p-space-150)', minWidth: '140px', verticalAlign: 'top' }}
          >
            <RHFTextField
              name={`rows.${index}.${key}`}
              type="number"
              min={0}
              allowDecimal={meta.decimals > 0}
              maxDecimals={meta.decimals}
              placeholder={i18n.t(`placeholder:${feeLabelKey(key)}`)}
              startAdornment={meta.symbol}
              inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
            />
          </td>
        ))}
      </IndexTable.Row>
    );
  });

  const summaryMarkup = totals.length > 0 && (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: SUMMARY_COLUMNS,
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          gridRow: `1 / span ${totals.length}`,
          display: 'flex',
          alignItems: 'center',
          padding: 'var(--p-space-150)',
          paddingLeft: 'var(--p-space-300)',
        }}
      >
        <Typography
          variant="body_md_semibold"
          color="text.secondary"
        >
          {i18n.t('return_inbound:fee.total')}
        </Typography>
      </Box>

      {totals.map((total) => (
        <Fragment key={total.currencyCode || 'none'}>
          <Box sx={{ padding: 'var(--p-space-150)' }}>
            <Typography
              variant="body_md"
              color="text.secondary"
            >
              {total.currencyCode || '-'}
            </Typography>
          </Box>
          {FEE_KEYS.map((key) => (
            <Box
              key={key}
              sx={{ padding: 'var(--p-space-150)', textAlign: 'right' }}
            >
              <Typography
                variant="body_md"
                color="text.secondary"
              >
                {`${metaOf(total.currencyCode).symbol} ${formatFee(
                  total.sums[key],
                  metaOf(total.currencyCode).decimals,
                )}`.trim()}
              </Typography>
            </Box>
          ))}
        </Fragment>
      ))}
    </Box>
  );

  return (
    <StackedModal
      isOpen={show}
      title={i18n.t('return_inbound:fee.edit_title')}
      sx={{ width: { xs: '95vw', sm: '900px' }, maxWidth: '95vw' }}
      content={
        <FormProvider methods={methods}>
          {saveError && (
            <Box sx={{ paddingBlockEnd: 'var(--p-space-300)' }}>
              <Banner tone="critical">{saveError}</Banner>
            </Box>
          )}
          <Box
            sx={(theme) => ({
              width: '100%',
              ...feeModalSx(theme),
              border: 'var(--p-border-width-025) solid var(--p-color-border)',
              borderRadius: 'var(--p-border-radius-300)',
              overflow: 'hidden',
              ...(fields.length > ROWS_VISIBLE && {
                maxHeight: ROWS_VISIBLE * ROW_HEIGHT + HEADER_HEIGHT,
                overflowY: 'auto',
              }),
              '& .Polaris-IndexTable__TableRow': { cursor: 'default' },
              '& .MuiAutocomplete-inputRoot': { paddingLeft: '0 !important' },
            })}
          >
            <IndexTable
              selectable={false}
              itemCount={fields.length}
              resourceName={{
                singular: i18n.t('return_inbound:field.history'),
                plural: i18n.t('return_inbound:field.history'),
              }}
              headings={[
                { title: i18n.t('return_inbound:field.history') },
                { title: i18n.t('return_inbound:fee.currency') },
                ...FEE_KEYS.map((key) => ({
                  title: i18n.t(`return_inbound:fee.${feeLabelKey(key)}`),
                  alignment: 'end',
                })),
              ]}
            >
              {rowMarkup}
            </IndexTable>
          </Box>
          <Box sx={{ paddingBlock: 'var(--p-space-300)' }}>
            <Divider />
          </Box>
          {summaryMarkup}
        </FormProvider>
      }
      primaryAction={{
        content: i18n.t('common:button.done'),
        disabled: !isDirty || saving,
        loading: saving,
        onAction: async () => {
          const valid = await methods.trigger();
          if (!valid) return;

          const payload = methods.getValues('rows').map((row) => ({
            receivingSequence: row.receivingSequence,
            codFee: toFeeNumber(row.codFee, metaOf(row.currencyCode).decimals),
            returnFee: toFeeNumber(row.returnFee, metaOf(row.currencyCode).decimals),
            prepaidAmount: toFeeNumber(row.prepaidAmount, metaOf(row.currencyCode).decimals),
            currencyCode: row.currencyCode || null,
          }));

          if (!onSave) {
            proceed({ confirmed: true, value: payload });
            return;
          }

          setSaveError('');
          setSaving(true);
          const failure = await onSave(payload);
          setSaving(false);
          if (failure) {
            setSaveError(failure);
            return;
          }
          proceed({ confirmed: true, value: payload });
        },
      }}
      onClose={() => proceed({ confirmed: false, value: null })}
    />
  );
};

export default confirmable(ReturnFeeEditDialog);
